import React, { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Avatar from '@amzn/awsui-chat-components-console/avatar';
import ChatBubble from '@amzn/awsui-chat-components-console/chat-bubble';
import PromptInput from '@amzn/awsui-components-console/prompt-input';
import Button from '@amzn/awsui-components-console/button';
import Box from '@amzn/awsui-components-console/box';
import Icon from '@amzn/awsui-components-console/icon';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import { useAIContext, AIContext } from '../../../stores/starnexus/aiContextStore';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

/** Build a context summary string from wizard state */
function buildContextPrompt(ctx: AIContext): string {
  const parts: string[] = [];
  if (ctx.datasetName) parts.push(`Dataset: "${ctx.datasetName}"`);
  if (ctx.fileName) parts.push(`Uploaded file: ${ctx.fileName}`);
  if (ctx.schema?.length) parts.push(`Schema (${ctx.schema.length} cols): ${ctx.schema.map(c => `${c.columnName}:${c.dataType}`).join(', ')}`);
  if (ctx.sqlTransform) parts.push(`Current SQL:\n${ctx.sqlTransform}`);
  if (ctx.filePreview?.length) {
    const headers = ctx.filePreview[0];
    const sample = ctx.filePreview.slice(1, 4).map(r => r.join(' | ')).join('\n');
    parts.push(`Sample data (headers: ${headers.join(', ')}):\n${sample}`);
  }
  return parts.length ? parts.join('\n\n') : '';
}

interface Message {
  role: 'user' | 'ai';
  content: string;
  displayedContent?: string;
  streaming?: boolean;
}

const SUGGESTIONS = [
  'What datasets do I have access to?',
  'Show processing status',
  'Help me write a SQL query',
  'Explain this error',
];

const MOCK_RESPONSES: Record<string, string> = {
  'What datasets do I have access to?':
    'You currently have access to 7 datasets:\n\n• bronze_rpt_cds_retail_cga\n• bronze_rpt_cds_lead_time_eu\n• bronze_rpt_cds_lead_time_us\n• bronze_rpt_cds_lsp_perf\n• bronze_rpt_cds_ecdd_etd_eu\n• bronze_rpt_cds_ecdd_etd_us\n• bronze_rpt_cds_carrier_perf\n\nAll are in ACTIVE lifecycle state on Andes.',
  'Show processing status':
    'Processing Status Summary:\n\n✅ 5 datasets processed successfully in the last 24h\n⏳ 1 dataset currently processing (carrier_perf)\n❌ 0 failures',
  'Help me write a SQL query':
    'Sure! What would you like to query? Tell me what data you need and I\'ll generate the SQL.',
  'Explain this error':
    'Please paste the error message and I\'ll analyze it.',
};

const DEFAULT_RESPONSE = 'I can help with dataset management, processing status, SQL queries, and error analysis. What would you like to know?';

export const AIAssistantContent: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const aiContext = useAIContext((s) => s.context);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  /** Typewriter: reveal text character by character */
  const typewrite = useCallback((fullText: string) => {
    let i = 0;
    const step = () => {
      // Reveal in chunks of 3-5 chars for speed
      const chunk = Math.min(i + 3 + Math.floor(Math.random() * 3), fullText.length);
      const partial = fullText.slice(0, chunk);
      setMessages(prev => prev.map((m, idx) => idx === prev.length - 1 ? { ...m, displayedContent: partial, streaming: chunk < fullText.length } : m));
      i = chunk;
      if (i < fullText.length) setTimeout(step, 15);
      else setMessages(prev => prev.map((m, idx) => idx === prev.length - 1 ? { ...m, streaming: false } : m));
    };
    step();
  }, []);

  const streamAI = useCallback(async (text: string) => {
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'ai', content: '', displayedContent: '', streaming: true }]);
    setLoading(false);
    const body = JSON.stringify({ message: text, history, context: buildContextPrompt(aiContext) });

    try {
      const res = await apiFetch(apiUrl('/api/ai/chat'), {
      ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      const reply = data.response || DEFAULT_RESPONSE;
      // Store full content, typewrite the display
      setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, content: reply } : m));
      typewrite(reply);
    } catch {
      const fallback = MOCK_RESPONSES[text] ?? DEFAULT_RESPONSE;
      setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, content: fallback, displayedContent: fallback, streaming: false } : m));
    }
  }, [messages, typewrite]);

  const handleSend = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setLoading(true);
    streamAI(trimmed);
  }, [loading, streamAI]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
        <SpaceBetween size="m">
          {messages.length === 0 && (
            <Box textAlign="center" padding="m" color="text-body-secondary">
              <SpaceBetween size="s">
                <Icon name="gen-ai" size="big" />
                <Box variant="h4">How can I help?</Box>
                <Box variant="small">Ask about datasets, processing, SQL, or errors.</Box>
              </SpaceBetween>
            </Box>
          )}

          {messages.map((msg, i) => (
            <ChatBubble
              key={i}
              type={msg.role === 'user' ? 'outgoing' : 'incoming'}
              avatar={msg.role === 'user'
                ? <Avatar initials='YY' ariaLabel='You' />
                : <Avatar color='gen-ai' iconName='gen-ai' ariaLabel='AI Assistant' />
              }
              ariaLabel={`Message from ${msg.role === 'user' ? 'you' : 'AI assistant'}`}
            >
              {msg.role === 'ai'
                ? <Markdown remarkPlugins={[remarkGfm]}>{msg.displayedContent ?? msg.content}</Markdown>
                : <Box variant="p">{msg.content}</Box>
              }
              {msg.streaming && <span style={{ borderRight: '2px solid currentColor', marginLeft: 1 }}>&nbsp;</span>}
            </ChatBubble>
          ))}

          {messages.length > 0 && messages[messages.length - 1]?.role === 'ai' && !loading && (
            <SpaceBetween size="xs" direction="horizontal">
              {['Tell me more', 'Generate SQL for this', 'Show the schema'].map((t) => (
                <Button key={t} onClick={() => handleSend(t)}>{t}</Button>
              ))}
            </SpaceBetween>
          )}

          {loading && (
            <ChatBubble type="incoming" avatar={<Avatar color='gen-ai' iconName='gen-ai' loading={true} ariaLabel='AI is thinking' />} ariaLabel="AI is thinking">
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Spinner />
                <Box>Thinking...</Box>
              </SpaceBetween>
            </ChatBubble>
          )}

          {messages.length === 0 && (
            <SpaceBetween size="xs">
              {SUGGESTIONS.map((t) => (
                <Button key={t} onClick={() => handleSend(t)}>{t}</Button>
              ))}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border-divider-default, #e9ebed)', padding: 12 }}>
        <PromptInput
          value={input}
          onChange={({ detail }) => setInput(detail.value)}
          onAction={() => handleSend(input)}
          actionButtonAriaLabel="Send"
          actionButtonIconName="send"
          placeholder="Ask anything..."
          disabled={loading}
        />
      </div>
    </div>
  );
};

// Legacy export for backward compat (now unused, AppShell uses AIAssistantContent via drawers)
export const AIAssistantDrawer: React.FC<{ isOpen: boolean; onClose: () => void }> = () => null;

export default AIAssistantContent;
